"use client";
import React, { useId, useState } from "react";
import { useTranslations } from "next-intl";
import { ChevronDown } from "lucide-react";
import { cn } from "@/app/lib/utils";
import type { ChatMessage, PersonaTraitSummary } from "@/types";

type ExpandedQueryInfo = NonNullable<ChatMessage["expandedQuery"]>;

type ExpandedQueryDebugProps = {
   expandedQuery: ExpandedQueryInfo;
};

const WEIGHT_THRESHOLDS = { HIGH: 0.7, MEDIUM: 0.4 } as const;

const hasMeaningfulSourceIntent = (
   sourceIntent?: ExpandedQueryInfo["sourceIntent"]
): boolean => {
   if (!sourceIntent) return false;
   return sourceIntent.type !== "none" && (sourceIntent.confidence ?? 0) > 0;
};

const getWeightStyle = (weight: number) => {
   if (weight >= WEIGHT_THRESHOLDS.HIGH)
      return { label: "HIGH", className: "text-emerald-600 bg-emerald-500/10" };
   if (weight >= WEIGHT_THRESHOLDS.MEDIUM)
      return { label: "MED", className: "text-amber-600 bg-amber-500/10" };
   return { label: "LOW", className: "text-slate-500 bg-slate-500/10" };
};

const PersonaTraitItem: React.FC<{ trait: PersonaTraitSummary }> = ({ trait }) => {
   const weightInfo = getWeightStyle(trait.weight);
   return (
      <div className="flex items-start gap-2 py-1">
         <span
            className={cn(
               "shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded",
               weightInfo.className
            )}
         >
            {weightInfo.label}
         </span>
         <span className="text-foreground/90 leading-relaxed break-words text-[11px]">
            {trait.content}
         </span>
      </div>
   );
};

export const ExpandedQueryDebug: React.FC<ExpandedQueryDebugProps> = ({
   expandedQuery
}) => {
   const translate = useTranslations("Chat.ExpandedQueryDebug");
   const [isOpen, setIsOpen] = useState(false);
   const contentId = useId();

   const personaTraits = expandedQuery.personaTraitsUsed ?? [];
   const traitItems = personaTraits.filter((t) => t.contentType === "trait");
   const exampleItems = personaTraits.filter((t) => t.contentType === "example");
   const sourceIntent = expandedQuery.sourceIntent;
   const shouldShowSourceIntent = hasMeaningfulSourceIntent(sourceIntent);

   const hasAnyField = Boolean(
      expandedQuery.expandedQueryEn ||
      expandedQuery.expandedQueryVi ||
      shouldShowSourceIntent ||
      expandedQuery.searchKeywords ||
      expandedQuery.hypotheticalContext ||
      (Array.isArray(expandedQuery.subQueries) && expandedQuery.subQueries.length > 0) ||
      personaTraits.length > 0
   );

   if (!hasAnyField) return null;

   return (
      <div className="flex-1 min-w-0">
         <button
            type="button"
            onClick={() => setIsOpen((v) => !v)}
            aria-expanded={isOpen}
            aria-controls={contentId}
            className="flex flex-wrap items-center gap-y-1 gap-x-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
         >
            <ChevronDown
               className={cn("w-3 h-3 transition-transform", isOpen && "rotate-180")}
            />
            <span className="opacity-80">{translate("title")}</span>
            <span className="opacity-50">
               · {isOpen ? translate("hide") : translate("show")}
            </span>
         </button>

         {isOpen && (
            <div id={contentId} className="mt-2 flex flex-col gap-2 text-xs">
               {expandedQuery.expandedQueryVi && (
                  <div className="p-2 bg-muted/30 rounded-lg">
                     <span className="block text-[10px] font-semibold text-foreground/60 uppercase tracking-wider mb-1">
                        {translate("expandedQueryVi")}
                     </span>
                     <p className="m-0 text-foreground/90 leading-relaxed break-words">
                        {expandedQuery.expandedQueryVi}
                     </p>
                  </div>
               )}
               {expandedQuery.expandedQueryEn && (
                  <div className="p-2 bg-muted/30 rounded-lg">
                     <span className="block text-[10px] font-semibold text-foreground/60 uppercase tracking-wider mb-1">
                        {translate("expandedQueryEn")}
                     </span>
                     <p className="m-0 text-foreground/90 leading-relaxed break-words">
                        {expandedQuery.expandedQueryEn}
                     </p>
                  </div>
               )}
               {shouldShowSourceIntent && sourceIntent && (
                  <div className="p-2 bg-sky-500/5 border border-sky-500/10 rounded-lg">
                     <span className="block text-[10px] font-semibold text-sky-600 uppercase tracking-wider mb-1">
                        {translate("sourceIntent")}
                     </span>
                     <p className="m-0 text-foreground/90 leading-relaxed break-words font-mono">
                        {sourceIntent.type} · {translate("confidence")}:{" "}
                        {Number(sourceIntent.confidence ?? 0).toFixed(2)}
                     </p>
                  </div>
               )}
               {expandedQuery.searchKeywords && (
                  <div className="p-2 bg-amber-500/5 border border-amber-500/10 rounded-lg">
                     <span className="block text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-1">
                        {translate("searchKeywords")}
                     </span>
                     <p className="m-0 text-foreground/90 leading-relaxed break-words font-mono">
                        {expandedQuery.searchKeywords}
                     </p>
                  </div>
               )}
               {expandedQuery.hypotheticalContext && (
                  <div className="p-2 bg-fuchsia-500/5 border border-fuchsia-500/10 rounded-lg">
                     <span className="block text-[10px] font-semibold text-fuchsia-600 uppercase tracking-wider mb-1">
                        {translate("hypotheticalContext")}
                     </span>
                     <p className="m-0 text-foreground/90 leading-relaxed break-words whitespace-pre-wrap">
                        {expandedQuery.hypotheticalContext}
                     </p>
                  </div>
               )}
               {Array.isArray(expandedQuery.subQueries) &&
                  expandedQuery.subQueries.length > 0 && (
                     <div className="p-2 bg-teal-500/5 border border-teal-500/10 rounded-lg">
                        <span className="block text-[10px] font-semibold text-teal-600 uppercase tracking-wider mb-1">
                           {translate("subQueries")}
                        </span>
                        <ol className="list-decimal pl-4 m-0 flex flex-col gap-0.5">
                           {expandedQuery.subQueries.map((sq, idx) => (
                              <li
                                 key={idx}
                                 className="text-foreground/90 leading-relaxed break-words"
                              >
                                 {sq}
                              </li>
                           ))}
                        </ol>
                     </div>
                  )}
               {personaTraits.length > 0 && (
                  <div className="p-2 bg-violet-500/5 border border-violet-500/10 rounded-lg">
                     <span className="block text-[10px] font-semibold text-violet-600 uppercase tracking-wider mb-1">
                        {translate("personaTraits")}
                        <span className="ml-1 text-foreground/40 normal-case font-normal">
                           ({personaTraits.length})
                        </span>
                     </span>
                     {traitItems.length > 0 && (
                        <div className="flex flex-col gap-0.5">
                           {traitItems.map((trait, idx) => (
                              <PersonaTraitItem key={`trait-${idx}`} trait={trait} />
                           ))}
                        </div>
                     )}
                     {exampleItems.length > 0 && (
                        <div className="mt-1.5 pt-1.5 border-t border-violet-500/10">
                           <span className="block text-[9px] font-medium text-violet-500/60 uppercase tracking-wider mb-0.5">
                              {translate("personaExamples")}
                           </span>
                           <div className="flex flex-col gap-0.5">
                              {exampleItems.map((example, idx) => (
                                 <p
                                    key={`example-${idx}`}
                                    className="m-0 text-[11px] text-foreground/70 leading-relaxed break-words italic pl-2 border-l-2 border-violet-500/20"
                                 >
                                    &ldquo;{example.content}&rdquo;
                                 </p>
                              ))}
                           </div>
                        </div>
                     )}
                  </div>
               )}
            </div>
         )}
      </div>
   );
};
